import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Lightbulb, Image as ImageIcon, Check, Trash2, User, Clock, Copy,
  CheckCircle2, RotateCcw, RefreshCw,
} from "lucide-react";
import useEscClose from "../hooks/useEscClose";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { confirmDialog } from "../utils/dialogs";

/**
 * SuggestionModal — a shared idea board.
 *
 * Layout (top → bottom):
 *   1. **Entry section** — textarea + optional screenshot + Send button.
 *      Anyone with a login can submit.
 *   2. **Community feed** — every past suggestion listed newest-first.
 *      Visible to ALL users so nobody re-submits an idea that's already
 *      on the pile.
 *
 * Admin extras on each feed row:
 *   • Mark done  — flags the item as resolved (renders greyed + tick badge)
 *   • Undo done  — un-marks a resolved item
 *   • Delete     — permanently removes the suggestion + its screenshot
 *
 * Regular users see the same feed read-only (with Copy for triage).
 */
const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024;

export default function SuggestionModal({ open, onClose }) {
  const { isAdmin } = useAuth();

  // Entry state
  const [text, setText] = useState("");
  const [dataUrl, setDataUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [justSent, setJustSent] = useState(false);
  const fileRef = useRef(null);

  // Feed state
  const [rows, setRows] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [busyRowId, setBusyRowId] = useState(null);

  useEscClose(open, onClose);

  const loadFeed = async () => {
    setFeedLoading(true);
    setFeedError("");
    try {
      const r = await api.get("/suggestions");
      setRows(r.data || []);
    } catch (e) {
      setFeedError(e?.response?.data?.detail || "Could not load feed");
    } finally {
      setFeedLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setText("");
      setDataUrl(null);
      setFileName("");
      setSaving(false);
      setError("");
      setJustSent(false);
      loadFeed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Only image files (PNG / JPG / WebP) are supported.");
      return;
    }
    if (f.size > MAX_SCREENSHOT_BYTES) {
      setError(`Screenshot must be ≤ ${Math.round(MAX_SCREENSHOT_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = () => { setDataUrl(reader.result); setFileName(f.name); };
    reader.onerror = () => setError("Could not read that image — try another file.");
    reader.readAsDataURL(f);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) { pickFile(f); e.preventDefault(); return; }
      }
    }
  };

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      setError("Please describe your suggestion in a few words.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post("/suggestions", {
        text: trimmed,
        screenshot_data_url: dataUrl,
      });
      // Clear the entry section, flash a small "thanks" line, and
      // reload the feed so the coach sees their idea land immediately.
      setText("");
      setDataUrl(null);
      setFileName("");
      setJustSent(true);
      setTimeout(() => setJustSent(false), 2500);
      await loadFeed();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sid) => {
    if (!(await confirmDialog("Delete this suggestion permanently?", { confirmLabel: "Delete", danger: true }))) return;
    setBusyRowId(sid);
    try {
      await api.delete(`/suggestions/${sid}`);
      setRows((prev) => prev.filter((r) => r.id !== sid));
      if (lightbox === sid) setLightbox(null);
    } catch (e) {
      setFeedError(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyRowId(null);
    }
  };

  const handleToggleDone = async (row) => {
    setBusyRowId(row.id);
    try {
      const next = !row.done;
      const r = await api.patch(`/suggestions/${row.id}/done`, { done: next });
      setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...r.data } : x)));
    } catch (e) {
      setFeedError(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusyRowId(null);
    }
  };

  const handleCopy = async (row) => {
    try {
      await navigator.clipboard.writeText(row.text);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId((cur) => (cur === row.id ? null : cur)), 1400);
    } catch { /* ignore */ }
  };

  const shotUrl = (sid) => `${api.defaults.baseURL}/suggestions/${sid}/screenshot`;

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
        data-testid="suggestion-modal"
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div className="card w-full max-w-2xl p-6 md:p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lightbulb size={18} className="text-amber-300" />
              <div className="font-display text-lg">Ideas &amp; suggestions</div>
              <span className="text-[11px] text-slate-500 font-mono-data">· {rows.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={loadFeed}
                className="pill !p-1.5"
                title="Refresh"
                data-testid="suggestion-refresh-btn"
              >
                <RefreshCw size={13} className={feedLoading ? "animate-spin" : ""} />
              </button>
              <button onClick={onClose} className="pill !p-1.5" data-testid="suggestion-close-btn">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* ---------------- Entry section ---------------- */}
          <div className="mb-5" data-testid="suggestion-entry-section">
            <div className="text-[12px] text-slate-400 mb-3">
              Share an idea to make this app better. Everyone sees the
              feed below so we don't duplicate work. Screenshots are
              welcome but optional.
            </div>

            <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              Your suggestion
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handlePaste}
              placeholder="e.g. Add a Whatsapp reminder when an athlete's supplement course ends…"
              rows={3}
              maxLength={4000}
              className="w-full px-3 py-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 resize-none"
              data-testid="suggestion-text-input"
            />
            <div className="flex items-center justify-between mt-1 mb-3">
              <div className="text-[10px] text-slate-600">Paste a screenshot with Ctrl+V, or attach one.</div>
              <div className="text-[10px] text-slate-600 font-mono-data">{text.length} / 4000</div>
            </div>

            {dataUrl ? (
              <div className="rounded-lg border border-[#1E304B] bg-[#0B1627] p-2 flex items-center gap-3 mb-3">
                <img
                  src={dataUrl}
                  alt="preview"
                  className="w-12 h-12 object-cover rounded border border-[#1E304B]"
                  data-testid="suggestion-screenshot-preview"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 truncate">{fileName || "Pasted image"}</div>
                  <div className="text-[10px] text-slate-500">Will be attached to your suggestion</div>
                </div>
                <button
                  className="pill !p-1.5"
                  onClick={() => { setDataUrl(null); setFileName(""); }}
                  title="Remove screenshot"
                  data-testid="suggestion-screenshot-remove-btn"
                  style={{ color: "#FCA5A5", borderColor: "rgba(239,68,68,0.4)" }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full px-3 py-2 rounded-lg border border-dashed border-[#1E304B] bg-[#0E1A2E] text-slate-400 hover:text-slate-200 hover:border-[#00E5FF]/40 transition text-sm flex items-center justify-center gap-2 mb-3"
                data-testid="suggestion-screenshot-pick-btn"
              >
                <ImageIcon size={14} /> Attach screenshot
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="suggestion-screenshot-file-input"
            />

            {error && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm" data-testid="suggestion-error">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-emerald-300" data-testid="suggestion-thanks">
                {justSent && (
                  <span className="inline-flex items-center gap-1">
                    <Check size={12} /> Thanks — added below.
                  </span>
                )}
              </div>
              <button
                className="pill"
                onClick={handleSubmit}
                disabled={saving || text.trim().length < 3}
                style={text.trim().length >= 3 && !saving ? {
                  background: "rgba(245,158,11,0.15)",
                  borderColor: "rgba(245,158,11,0.45)",
                  color: "#FCD34D",
                } : undefined}
                data-testid="suggestion-submit-btn"
              >
                <Lightbulb size={13} /> {saving ? "Sending…" : "Send suggestion"}
              </button>
            </div>
          </div>

          {/* ---------------- Community feed ---------------- */}
          <div className="border-t border-[#1E304B] pt-4" data-testid="suggestion-feed">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
              <Lightbulb size={10} /> Everyone's ideas
            </div>

            {feedError && (
              <div className="mb-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm">
                {feedError}
              </div>
            )}

            {feedLoading && rows.length === 0 ? (
              <div className="text-center text-slate-500 text-sm py-6">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-center text-slate-500 text-sm py-6">
                No suggestions yet — be the first to share one.
              </div>
            ) : (
              <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                {rows.map((row) => {
                  const when = row.created_at
                    ? new Date(row.created_at).toLocaleString("en-GB", {
                        day: "2-digit", month: "short",
                        hour: "2-digit", minute: "2-digit",
                      })
                    : "";
                  const isDone = Boolean(row.done);
                  return (
                    <div
                      key={row.id}
                      className={`p-3 rounded-lg border ${
                        isDone
                          ? "bg-emerald-500/5 border-emerald-500/25 opacity-75"
                          : "bg-[#0B1627] border-[#1E304B]"
                      }`}
                      data-testid={`suggestion-row-${row.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm whitespace-pre-wrap break-words ${
                            isDone ? "text-slate-400 line-through decoration-emerald-500/40" : "text-slate-100"
                          }`}>
                            {row.text}
                          </div>
                        </div>
                        {row.screenshot_id && (
                          <button
                            onClick={() => setLightbox(row.id)}
                            className="shrink-0 w-14 h-14 rounded border border-[#1E304B] overflow-hidden bg-[#0E1A2E] hover:border-[#00E5FF]/40 transition"
                            title="View screenshot"
                            data-testid={`suggestion-shot-thumb-${row.id}`}
                          >
                            <img
                              src={shotUrl(row.id)}
                              alt="screenshot"
                              className="w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono-data flex-wrap">
                          <span className="inline-flex items-center gap-1">
                            <User size={10} /> {row.submitted_by?.name || row.submitted_by?.mobile || "unknown"}
                            {row.submitted_by?.role && (
                              <span className="ml-1 px-1 py-0 rounded bg-[#122036] border border-[#1E304B] text-cyan-200/70 text-[9px] uppercase">
                                {row.submitted_by.role}
                              </span>
                            )}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Clock size={10} /> {when}
                          </span>
                          {isDone && (
                            <span className="inline-flex items-center gap-1 text-emerald-300 font-display" data-testid={`suggestion-done-badge-${row.id}`}>
                              <CheckCircle2 size={10} /> Done
                              {row.done_by?.name && <span className="opacity-60">· {row.done_by.name}</span>}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleCopy(row)}
                            className="pill !py-1 !px-2 !text-[11px]"
                            data-testid={`suggestion-copy-btn-${row.id}`}
                          >
                            {copiedId === row.id
                              ? (<><Check size={11} /> Copied</>)
                              : (<><Copy size={11} /> Copy</>)}
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => handleToggleDone(row)}
                                disabled={busyRowId === row.id}
                                className="pill !py-1 !px-2 !text-[11px]"
                                style={isDone ? {
                                  color: "#CBD5E1",
                                  borderColor: "rgba(148,163,184,0.4)",
                                } : {
                                  background: "rgba(16,185,129,0.12)",
                                  borderColor: "rgba(16,185,129,0.45)",
                                  color: "#6EE7B7",
                                }}
                                data-testid={`suggestion-done-btn-${row.id}`}
                                title={isDone ? "Mark as not done yet" : "Mark this suggestion as done"}
                              >
                                {isDone
                                  ? (<><RotateCcw size={11} /> Un-mark</>)
                                  : (<><CheckCircle2 size={11} /> Mark done</>)}
                              </button>
                              <button
                                onClick={() => handleDelete(row.id)}
                                disabled={busyRowId === row.id}
                                className="pill !py-1 !px-2 !text-[11px]"
                                style={{ color: "#FCA5A5", borderColor: "rgba(239,68,68,0.4)" }}
                                data-testid={`suggestion-delete-btn-${row.id}`}
                              >
                                <Trash2 size={11} /> {busyRowId === row.id ? "…" : "Delete"}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[210] bg-[#040D1A]/95 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="suggestion-lightbox"
        >
          <img
            src={shotUrl(lightbox)}
            alt="screenshot"
            className="max-w-full max-h-full object-contain rounded shadow-2xl"
          />
          <button
            className="pill !p-1.5 absolute top-6 right-6"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </>,
    document.body,
  );
}
